import React, {useContext, useEffect, useState} from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Dialog,
    DialogContent,
    Menu,
    MenuItem,
} from "@mui/material";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import {PAContext} from "../../../Services/PAContext";
import ReceiptDialogEditNotes from "./ReceiptDialogEditNotes";
import ReceiptDialogEditCosts from "./ReceiptDialogEditCosts";
import ReceiptDialogEditCategory from "./ReceiptDialogEditCategory";
import ReceiptDialogEditPrivacy from "./ReceiptDialogEditPrivacy";
import ReceiptDialogEditLinkItem from "./ReceiptDialogEditLinkItem";
import ReceiptDialogEditLocation from "./ReceiptDialogEditLocation";
import ReceiptDialogEditDocument from "./ReceiptDialogEditDocument";
import dayjs from "dayjs";


const formatAmount = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2, // ⬅️ This forces the "0" in 33.90
        maximumFractionDigits: 2,
    }).format(parseFloat(amount) || 0);
};
const sectionColorMap = {
    Meals: '#e8f2ed',
    Activities: '#cbe0f7',
    Lodging: '#fef4ec',
    General: '#EDE7F6',
    Flights: '#fcf9dd',
    Supplies:'#FEE5D4',
    Shopping:'#B2D4F0',
    Incidentals:'#FAF3CC',
    Transportation:'#D6F5E4'
};

function ReceiptCard({ section, receipts, onEdit, onDelete }) {

    const { PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, authenticated } = dataArray;

    // Calculate total amounts per currency
    const totalsByCurrency = receipts.reduce((acc, receipt) => {
        const currency = receipt.currency || "USD"; // Default to USD if missing
        const amount = parseFloat(receipt.amount) || 0;

        if (acc[currency]) {
            acc[currency] += amount;
        } else {
            acc[currency] = amount;
        }

        return acc;
    }, {});

    // Get event lodging, meals, flights, and activities
    const eventLodging = PA_GlobalObject.Planning.lodging || [];
    const eventMeals = PA_GlobalObject.Planning.meals || [];
    const eventActivities = PA_GlobalObject.Planning.activities || [];
    const eventFlights = PA_GlobalObject.Planning.flights || [];


    /*
        this is for the Menu list for each individual Card
     */
    const [anchorReceiptElements, setAnchorReceiptElements] = useState(receipts);
    const handleOpenReceiptMenu = (event, index) => {
        const updatedAnchors = [...anchorReceiptElements];
        updatedAnchors[index] = event.currentTarget;
        setAnchorReceiptElements(updatedAnchors);
    };
    // Function to handle closing the menu for a specific receipt.
    const handleCloseReceiptMenu = (index) => {
        const updatedAnchors = [...anchorReceiptElements];
        updatedAnchors[index] = null;
        setAnchorReceiptElements(updatedAnchors);
    };
    function handleEditReceipt(receipt_id, updatedData) {
        handleCloseReceiptMenu(receipt_id); // Closes the 3-dot menu
        onEdit(receipt_id, updatedData);    // Passes ID AND the new data to ReceiptList
    }

    /*
        for looking at the full size image of the receipt
     */

    const [viewingReceiptId, setViewingReceiptId] = useState(null);
    const [imageSrc, setImageSrc] = useState("");

    useEffect(() => {
        // Find if the specific receipt we are waiting for just got its URL
        const activeReceipt = receipts.find(r => r.id === viewingReceiptId);

        if (activeReceipt && activeReceipt.signedUrl) {
            const url = activeReceipt.signedUrl;
            const fileName = (activeReceipt.file_name || "").toLowerCase();
            const isDoc = fileName.endsWith(".pdf") || fileName.endsWith(".eml");

            if (isDoc) {
                window.open(url, "_blank");
                setViewingReceiptId(null); // Reset
            } else {
                setImageSrc(url);
                // We don't need a separate setOpenImage(true) because
                // the Dialog will check if viewingReceiptId === activeReceipt.id
            }
        }
    }, [receipts, viewingReceiptId]);

    const handleOpenImage = (receipt) => {
        const data = receipt.full_data || receipt.thumbnail_data;
        if (!data) return;

        setViewingReceiptId(receipt.id); // 🎯 Mark this specific receipt as "active"

        const fileName = receipt.file_name.toLowerCase();
        const isDoc = fileName.endsWith(".pdf") || fileName.endsWith(".eml");

        // Fallback for Old Data
        if (data.startsWith("data:") || data.startsWith("http")) {
            if (isDoc) {
                window.open(data, "_blank");
                setViewingReceiptId(null);
            } else {
                setImageSrc(data);
            }
            return;
        }

        // Request Signed URL
        if (PA_GlobalObject.Planning.status!== 10) {
            sendSocketMessage({
                action: "E_requestSignedUrlForReceiptForGCS",
                event_id: PA_GlobalObject.Planning.id,
                receipt_id: receipt.id,
                gcs_path: data
            });
        }
    };
    const handleCloseImage = () => {
        setViewingReceiptId(null); // 🛑 Closes the dialog
        setImageSrc("");
    };


    const [selectedAction, setSelectedAction] = useState("");
    const [receiptTabSelected, setReceiptTabSelected] = useState("");



    const tabIcons = [
        { key: 'Category', icon: 'icon-drawer-full', label: 'Category', color: '#FF9800' }, // Vibrant Orange
        { key: 'Amount', icon: 'icon-wallet', label: 'Amount', color: '#4CAF50' },          // Financial Green
        { key: 'Location', icon: 'icon-map-marker', label: 'Location', color: '#F44336' }, // Map Red
        { key: 'Link', icon: 'icon-link', label: 'Link', color: '#009688' },             // Teal
        { key: 'Photo', icon: 'icon-picture2', label: 'Photo', color: '#E91E63' },         // Deep Pink / Magenta
        { key: 'Privacy', icon: 'icon-lock', label: 'Privacy', color: '#607D8B' },         // Blue Grey (Security/Steel)
        { key: 'Notes', icon: 'icon-pencil2', label: 'Notes', color: '#2196F3' },         // Info Blue
    ];

    const [isReceiptDialogEditCategoryOpen, setReceiptDialogEditCategoryOpen] = useState(false);
    const [isReceiptDialogEditLocationOpen, setReceiptDialogEditLocationOpen] = useState(false);
    const [isReceiptDialogEditLinkOpen, setReceiptDialogEditLinkOpen] = useState(false);
    const [isReceiptDialogEditCostsOpen, setReceiptDialogEditCostsOpen] = useState(false);
    const [isReceiptDialogEditPhotoOpen, setReceiptDialogEditPhotoOpen] = useState(false);
    const [isReceiptDialogEditNotesOpen, setReceiptDialogEditNotesOpen] = useState(false);
    const [isReceiptDialogEditPrivacyOpen, setReceiptDialogEditPrivacyOpen] = useState(false);

    const [editReceiptNotes, setEditReceiptNotes] = useState(null);
    const [editReceiptCosts, setEditReceiptCosts] = useState(null);
    const [editReceiptCategory, setEditReceiptCategory] = useState(null);
    const [editReceiptLocation, setEditReceiptLocation] = useState(null); // Add this
    const [editReceiptLink, setEditReceiptLink] = useState(null);         // Add this
    const [editReceiptPhoto, setEditReceiptPhoto] = useState(null);       // Add this
    const [editReceiptPrivacy, setEditReceiptPrivacy] = useState(null);

    const handleMenuItemClick = (intent, _receipt_id) => {
        // 1. Close the 3-dot menu and set the visual action state
        handleCloseReceiptMenu(_receipt_id);
        setSelectedAction(intent);

        // 2. Find the target receipt once to avoid repeated .find calls
        const targetReceipt = receipts.find((rec) => rec.id === _receipt_id);
        if (!targetReceipt) return;

        // 3. Dispatch the correct Dialog opener
        switch (intent) {
            case "Location":
                setEditReceiptLocation(targetReceipt); // Assuming you add this state
                setReceiptDialogEditLocationOpen(true);
                break;

            case "Link":
                setEditReceiptLink(targetReceipt); // Assuming you add this state
                setReceiptDialogEditLinkOpen(true);
                break;

            case "Amount":
                setEditReceiptCosts(targetReceipt);
                setReceiptDialogEditCostsOpen(true);
                break;

            case "Photo":
                setEditReceiptPhoto(targetReceipt); // Useful for the viewer/editor
                setReceiptDialogEditPhotoOpen(true);
                break;

            case "Notes":
                setEditReceiptNotes(targetReceipt);
                setReceiptDialogEditNotesOpen(true);
                break;

            case "Category":
                setEditReceiptCategory(targetReceipt);
                setReceiptDialogEditCategoryOpen(true);
                break;

            case "Privacy":
                setEditReceiptPrivacy(targetReceipt);
                setReceiptDialogEditPrivacyOpen(true);
                break;
            default:
                // Handle tab selection for items that aren't dialogs
                receiptTabSelected === intent
                    ? setReceiptTabSelected("")
                    : setReceiptTabSelected(intent);
                break;
        }
    };


    return (
        <div>
            <Card
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginBottom: "20px",
                    borderRadius: '20px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid #f0f0f0',
                    overflow: 'hidden'
                }}
            >
                {/* Header: Section Title & Totals */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={3}
                    py={2}
                    sx={{
                        background: `linear-gradient(to right, ${sectionColorMap[section]}99, #ffffff)`,
                        borderBottom: '1px solid #f0f0f0'
                    }}
                >
                    <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#151269', letterSpacing: '0.5px' }}>
                        {section.toUpperCase()}
                    </Typography>
                    <Box sx={{ textAlign: "right" }}>
                        {Object.entries(totalsByCurrency).map(([currency, total]) => (
                            <Typography key={currency} sx={{ fontWeight: 900, fontSize: '18px', color: '#151269' }}>
                                {formatAmount(total, currency)}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <CardContent sx={{ padding: "16px !important" }}>
                    {receipts
                        .sort((a, b) => {
                            // Handle cases where date might be missing (pushing them to the end)
                            if (!a.date) return 1;
                            if (!b.date) return -1;
                            return dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
                        })
                        .map((_receipt) => {
                        const receipt = JSON.parse(JSON.stringify(_receipt));
                        const receipt_location = receipt.location || {};

                        // Logic to find the name of the linked item
                        const linkedName = (() => {
                            if (receipt_location.name) return receipt_location.name;

                            const sectionDataMap = {
                                Meals: { data: eventMeals, displayKeys: ["title", "name"] },
                                Lodging: { data: eventLodging, displayKeys: ["name"] },
                                Activities: { data: eventActivities, displayKeys: ["title"] },
                                Flights: { data: eventFlights, displayKeys: ["airline_name", "confirmation_number"] }
                            };

                            const sectionDetails = sectionDataMap[receipt.section];
                            if (sectionDetails) {
                                const relatedEntry = sectionDetails.data.find(item => item.id === receipt.section_item);
                                if (relatedEntry) {
                                    return sectionDetails.displayKeys.map(key => relatedEntry[key]).find(Boolean);
                                }
                            }
                            return null;
                        })();

                        const isBusiness = receipt.is_business_expense === true;

                        return (
                            <Box
                                key={receipt.id}
                                sx={{
                                    borderRadius: '16px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #f0f0f0',
                                    padding: '12px',
                                    marginBottom: '10px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 15px rgba(0,0,0,0.06)',
                                        borderColor: '#15126940'
                                    }
                                }}
                            >
                                <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                                    {/* Main Info */}
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#151269',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '14px'
                                            }}
                                        >
                                            {linkedName || "General Receipt"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#888', fontWeight: 500 }}>
                                            {receipt.date ? dayjs(receipt.date).format('DD MMM YYYY') : 'No Date Set'}
                                        </Typography>
                                    </Box>

                                    {/* Amount & Menu */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '15px',
                                                color: isBusiness ? '#151269' : '#444',
                                                mr: 0.5
                                            }}
                                        >
                                            {new Intl.NumberFormat(undefined, {
                                                style: 'currency',
                                                currency: receipt.currency || 'USD',
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(parseFloat(receipt.amount || 0))}
                                        </Typography>

                                        {receipt.attendee_id === Account.id && (
                                            <IconButton
                                                size="small"
                                                onClick={(event) => handleOpenReceiptMenu(event, receipt.id)}
                                                sx={{
                                                    ml: 0.5,
                                                    bgcolor: '#f8f9fa',
                                                    '&:hover': { bgcolor: '#eee' }
                                                }}
                                            >
                                                <i className="icon-menu2" style={{ fontSize: '14px', color: '#151269' }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>

                                {/* Notes Section */}
                                {receipt.notes && (
                                    <Box sx={{
                                        mt: 1,
                                        ml: 1,
                                        pl: 1.5,
                                        borderLeft: '2px solid #15126920',
                                        py: 0.5
                                    }}>
                                        <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.4 }}>
                                            {receipt.notes}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Status Pills Container */}
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',       // 🎯 This prevents the "pilling out" of the container
                                    justifyContent: 'flex-end',
                                    gap: 0.8,              // Horizontal gap
                                    rowGap: 1,             // Vertical gap if they wrap to a second line
                                    mt: 1.5,
                                    ml: 'auto',            // Ensures the group stays pinned to the right
                                    maxWidth: '100%'       // Prevents the container itself from stretching the card
                                }}>
                                    {/* Muted Business Pill */}
                                    {isBusiness && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(21, 18, 105, 0.08)', px: 1, py: 0.3, borderRadius: '6px', border: '1px solid rgba(21, 18, 105, 0.1)' }}>
                                            <i className="icon-briefcase" style={{ color: '#151269', fontSize: '10px', opacity: 0.8 }} />
                                            <Typography sx={{ color: '#151269', fontSize: '10px', fontWeight: 800, opacity: 0.9 }}>BUSINESS</Typography>
                                        </Box>
                                    )}

                                    {receipt.private && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#F3E5F5', px: 1, py: 0.3, borderRadius: '6px' }}>
                                            <i className="icon-lock" style={{ color: '#9c27b0', fontSize: '10px' }} />
                                            <Typography sx={{ color: '#9c27b0', fontSize: '10px', fontWeight: 800 }}>PRIVATE</Typography>
                                        </Box>
                                    )}
                                    {receipt.section_item !== "" && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#E8F5E9', px: 1, py: 0.3, borderRadius: '6px' }}>
                                            <i className="icon-link" style={{ color: '#4caf50', fontSize: '10px' }} />
                                            <Typography sx={{ color: '#4caf50', fontSize: '10px', fontWeight: 800 }}>LINKED</Typography>
                                        </Box>
                                    )}
                                    {receipt.file_name && (
                                        <Box
                                            onClick={(e) => { e.stopPropagation(); handleOpenImage(receipt); }}
                                            sx={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                bgcolor: '#E3F2FD',
                                                px: 1, py: 0.3,
                                                borderRadius: '6px',
                                                '&:hover': { bgcolor: '#BBDEFB' }
                                            }}
                                        >
                                            <i className="icon-file-check" style={{ color: '#2196f3', fontSize: '10px' }} />
                                            <Typography sx={{ color: '#2196f3', fontSize: '10px', fontWeight: 800 }}>
                                                {receipt.file_name.split('.').pop().toUpperCase()}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Standard Menu Implementation */}
                                {receipt.attendee_id === Account.id && (
                                    <Menu
                                        anchorEl={anchorReceiptElements[receipt.id]}
                                        open={Boolean(anchorReceiptElements[receipt.id])}
                                        onClose={() => handleCloseReceiptMenu(receipt.id)}
                                        PaperProps={{
                                            sx: { borderRadius: '20px', width: '320px', mt: 1, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }
                                        }}
                                    >
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', p: 1, borderBottom: '1px solid #eee' }}>
                                            {tabIcons.map(({ key, icon, label, color }) => (
                                                <Box
                                                    key={key}
                                                    onClick={() => handleMenuItemClick(key, receipt.id)}
                                                    sx={{
                                                        display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.5, borderRadius: '12px', cursor: 'pointer',
                                                        transition: 'all 0.2s', '&:hover': { backgroundColor: `${color}10`, transform: 'scale(1.05)' }
                                                    }}
                                                >
                                                    <Box sx={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${color}20`, borderRadius: '12px', mb: 0.5 }}>
                                                        <i className={icon} style={{ fontSize: '20px', color: color }} />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '10px' }}>{label}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        <MenuItem
                                            onClick={() => {
                                                handleCloseReceiptMenu(receipt.id);
                                                onDelete(receipt.id)
                                            }}
                                            sx={{ m: 1, borderRadius: '10px', color: '#d32f2f' }}>
                                            <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}><i className="icon-trash" /></ListItemIcon>
                                            <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>Delete Receipt</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                )}
                            </Box>
                        );
                    })}
                </CardContent>
            </Card>



            {/* Dialog for Full-Screen Image */}
            <Dialog
                open={Boolean(viewingReceiptId)}
                onClose={handleCloseImage}
                maxWidth="md"
                fullWidth
                TransitionProps={{ timeout: 400 }} // Smoother fade in
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        overflow: 'hidden',
                        bgcolor: '#1a1a1a', // Dark cinema background
                        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                        position: 'relative'
                    }
                }}
            >
                {/* Floating Header Bar */}
                <Box sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)'
                }}>
                    <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '14px', letterSpacing: '1px' }}>
                        RECEIPT VIEW
                    </Typography>
                    <IconButton
                        onClick={handleCloseImage}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                        }}
                    >
                        <i className="icon-cross" style={{ fontSize: '16px' }} />
                    </IconButton>
                </Box>

                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '500px',
                        overflow: 'hidden'
                    }}
                >
                    {imageSrc ? (
                        <Box
                            sx={{
                                p: 4,
                                pt: 10, // Leave room for header
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                overflowY: 'auto',
                                maxHeight: '90vh'
                            }}
                        >
                            <Box sx={{
                                position: 'relative',
                                transition: 'transform 0.3s ease',
                                '&:hover': { transform: 'scale(1.02)' } // Subtle interactive feel
                            }}>
                                <img
                                    src={imageSrc}
                                    alt="Receipt"
                                    style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                        border: '4px solid rgba(255,255,255,0.05)'
                                    }}
                                />
                            </Box>
                        </Box>
                    ) : (
                        /* Fun & Modern Loading State */
                        <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                            {/* A "Skeleton" receipt shape that pulses */}
                            <Box sx={{
                                width: '200px',
                                height: '280px',
                                bgcolor: 'rgba(255,255,255,0.05)',
                                borderRadius: '12px',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
                                    animation: 'shimmer 1.5s infinite'
                                }
                            }}>
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{ width: '60%', height: '10px', bgcolor: 'rgba(255,255,255,0.1)', mb: 1, borderRadius: '4px' }} />
                                    <Box sx={{ width: '40%', height: '8px', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '4px' }} />
                                </Box>
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 900,
                                        color: 'white',
                                        letterSpacing: '2px',
                                        textTransform: 'uppercase',
                                        textShadow: '0 2px 10px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    Decrypting Document
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                                    Verifying secure cloud link...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                {/* Custom Animation CSS */}
                <style>
                    {`
                        @keyframes shimmer {
                            0% { transform: translateX(-100%); }
                            100% { transform: translateX(100%); }
                        }
                        @keyframes fadeIn {
                            from { opacity: 0; transform: translateY(10px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                    `}
                </style>
            </Dialog>

            <ReceiptDialogEditCategory
                isOpen={isReceiptDialogEditCategoryOpen}
                onClose={() => setReceiptDialogEditCategoryOpen(false)}
                receipt={editReceiptCategory} // The receipt object set from the menu click
                onSave={(id, updatedFields) => handleEditReceipt(id, updatedFields)}
            />

            <ReceiptDialogEditCosts
                isOpen={isReceiptDialogEditCostsOpen}
                onClose={() => setReceiptDialogEditCostsOpen(false)}
                receipt={editReceiptCosts} // The receipt object set from the menu click
                onSave={(id, updatedFields) => handleEditReceipt(id, updatedFields)}
            />

            <ReceiptDialogEditLocation
                isOpen={isReceiptDialogEditLocationOpen}
                onClose={() => setReceiptDialogEditLocationOpen(false)}
                receipt={editReceiptLocation} // The receipt object set from the menu click
                onSave={(id, updatedFields) => handleEditReceipt(id, updatedFields)}
            />

            <ReceiptDialogEditLinkItem
                isOpen={isReceiptDialogEditLinkOpen}
                onClose={() => setReceiptDialogEditLinkOpen(false)}
                receipt={editReceiptLink} // The receipt object set from the menu click
                onSave={(id, updatedFields) => handleEditReceipt(id, updatedFields)}
            />

            <ReceiptDialogEditDocument
                isOpen={isReceiptDialogEditPhotoOpen}
                onClose={() => setReceiptDialogEditPhotoOpen(false)}
                receipt={editReceiptPhoto} // The receipt object set from the menu click
                onSave={(id, updatedFields) => handleEditReceipt(id, updatedFields)}
            />

            <ReceiptDialogEditNotes
                isOpen={isReceiptDialogEditNotesOpen}
                onClose={() => setReceiptDialogEditNotesOpen(false)}
                receipt={editReceiptNotes} // This is the full object set in handleMenuItemClick
                onSave={(id, updatedFields) => handleEditReceipt(id, updatedFields)}
            />

            <ReceiptDialogEditPrivacy
                isOpen={isReceiptDialogEditPrivacyOpen}
                onClose={() => setReceiptDialogEditPrivacyOpen(false)}
                receipt={editReceiptPrivacy} // This is the full object set in handleMenuItemClick
                onSave={(id, updatedFields) => handleEditReceipt(id, updatedFields)}
            />

        </div>

    );
}

export default ReceiptCard;
